import React from 'react'
import {
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Tooltip,
} from '@mui/material'
import CustomTableCell from './CustomTableCell'
import CustomHeadCell from './CustomHeadCell'
import { formatPhoneNumber } from '../../../../utils/FormatPhoneNumber/FormatPhoneNumber'
import { formatDate } from '../../../../utils/FormatDate'
import { t } from 'i18next'
import { CheckCircle, HelpCircle } from 'react-feather'
import { Trans } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { tableBodyStyle, tableRowStyle } from './LeadsTable.styles'
import { LoadingTable } from '../../../../components/LoadingElements'
import useCommonVars from '../../../../utils/FeatureFlags/useCommonVars'

type LeadsTableProps = {
	leads: any[]
	handleRowClick: (leadId: string) => void
	enableLoadMore: boolean
	onLoadMore: () => void
	isLoading: boolean
}

export function LeadsTable({ leads = [], handleRowClick, enableLoadMore, onLoadMore, isLoading }: LeadsTableProps) {
	const { palette } = useTheme()
	const { PROPAI_HELP_EMAIL } = useCommonVars()
	return (
		<React.Fragment>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<CustomHeadCell>{t('campaign_details.table_header_name')}</CustomHeadCell>
							<CustomHeadCell>
								{t('campaign_details.table_header_status')}
								<Tooltip
									title={
										<Trans
											i18nKey="campaign_details.table_header_status_tooltip"
											components={{
												mail: (
													<a
														style={{ color: 'white' }}
														href={`mailto:${PROPAI_HELP_EMAIL}`}
													/>
												),
											}}
										/>
									}
									placement="top"
									arrow
								>
									<HelpCircle
										style={{
											alignSelf: 'center',
											color: palette.brand.main,
											width: '1rem',
											marginLeft: '5px',
											verticalAlign: 'bottom',
										}}
									/>
								</Tooltip>
							</CustomHeadCell>
							<CustomHeadCell>{t('campaign_details.table_header_address')}</CustomHeadCell>
							<CustomHeadCell>{t('campaign_details.table_header_call_summary')}</CustomHeadCell>
							<CustomHeadCell>{t('campaign_details.table_header_time_called')}</CustomHeadCell>
						</TableRow>
					</TableHead>
					{isLoading && <LoadingTable rows={10} columns={5} />}
					{!!leads?.length && (
						<TableBody sx={tableBodyStyle}>
							{leads.map((lead) => (
								<TableRow key={lead.lead_id} sx={tableRowStyle}>
									<TableCell
										onClick={() => handleRowClick(lead.lead_id)}
										sx={{ color: palette.text.darkGrey, border: 'none' }}
									>
										<span>{lead.prospect_name}</span>
										<br />
										<span style={{ fontStyle: 'italic' }}>
											{formatPhoneNumber(lead.phone_number)}
										</span>
									</TableCell>
									<TableCell
										onClick={() => handleRowClick(lead.lead_id)}
										sx={{ color: palette.text.darkGrey, border: 'none' }}
									>
										<CheckCircle width={20} height={20} style={{ verticalAlign: 'middle' }} />
										<span style={{ marginLeft: '4px' }}>
											{lead.lead_status ? lead.lead_status : 'New'}
										</span>
									</TableCell>
									<CustomTableCell
										onClick={() => handleRowClick(lead.lead_id)}
										text={lead.address ? lead.address : ''}
									/>
									<CustomTableCell
										onClick={() => handleRowClick(lead.lead_id)}
										text={lead.lead_status === 'Called - No Answer' ? '' : lead.call_summary}
									/>
									<CustomTableCell
										onClick={() => handleRowClick(lead.lead_id)}
										text={lead.last_call_dt ? formatDate(lead.last_call_dt) : ''}
									/>
								</TableRow>
							))}
						</TableBody>
					)}
					{!leads?.length && (
						<TableBody sx={tableBodyStyle}>
							<TableRow sx={tableRowStyle}>
								<TableCell
									sx={{ color: palette.text.darkGrey, border: 'none', textAlign: 'center' }}
									colSpan={5}
								>
									<Trans i18nKey="campaign_details.table_body_empty_message" />
								</TableCell>
							</TableRow>
						</TableBody>
					)}
				</Table>
				{enableLoadMore && (
					<TableFooter sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<TableRow>
							<Button onClick={onLoadMore} fullWidth color="primary">
								{t('campaign_details.button_load_more')}
							</Button>
						</TableRow>
					</TableFooter>
				)}
			</TableContainer>
		</React.Fragment>
	)
}
